import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentCashEquivalentData,
  type EnrollmentCashEquivalentSubmitInput,
  type EnrollmentCashEquivalentSubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment CashEquivalent data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentCashEquivalentData>
 * @example const response = await getEnrollmentCashEquivalentData()
 */
export const getEnrollmentCashEquivalentData = async (
  context?: DefaultContext,
): Promise<EnrollmentCashEquivalentData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentCashEquivalentData',
      query: `mutation getEnrollmentCashEquivalentData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              userInfo {
                investment {
                  riskTolerance {
                    allocation {
                      equityPercent
                      fixedIncomePercent
                      cashEquivalentPercent
                    }
                  }
                }
              }
              recommendation {
                investment {
                  cashEquivalentType {
                    recommended
                    allowTripleTaxFreeMunicipalMutualFund
                  }
                }
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentCashEquivalentData }
  return response.submitEnrollment
}

export const useGetEnrollmentCashEquivalentData = createQueryHook<undefined, EnrollmentCashEquivalentData>(
  getEnrollmentCashEquivalentData,
)

/**
 * Submit Enrollment CashEquivalent data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentCashEquivalentSubmitOutput>
 * @example const response = await submitEnrollmentCashEquivalentData();
 */
export const submitEnrollmentCashEquivalentData = async (
  enrollmentCashEquivalentSubmitInput: EnrollmentCashEquivalentSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentCashEquivalentSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentCashEquivalentData',
      query: `mutation submitEnrollmentCashEquivalentData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentCashEquivalentSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentCashEquivalentSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentCashEquivalentData = createMutationHook<
  EnrollmentCashEquivalentSubmitInput,
  EnrollmentCashEquivalentSubmitOutput
>(submitEnrollmentCashEquivalentData)
